import { CartItem, StoreSeller, PaymentMethod, PaymentMethodLabel, StoreProductDetail, StoreVariantColor } from '../models';
import { formatCurrency } from './currency';
import { WHATSAPP_NUMBER } from '../constants/contact';

interface PaymentEntry {
  paymentMethod: PaymentMethod;
  amountPaid: number;
  installments?: 1 | 2;
}

export function buildWhatsAppUrl(
  seller: StoreSeller,
  items: CartItem[],
  orderId?: number,
  paymentEntries?: PaymentEntry[],
  customerName?: string,
  customerPhone?: string,
  observation?: string,
  hasPromotionalItems?: boolean
): string {
  const lines = items.map((i) => {
    const sku = i.variantSku ? ` [${i.variantSku}]` : '';
    const giftTag = i.isGift ? ' (presente)' : '';
    return `• ${i.productName}${sku} (${i.variantLabel}) x${i.quantity} — R$ ${formatCurrency((i.promotionalPrice ?? i.price) * i.quantity)}${giftTag}`;
  });

  const total = formatCurrency(
    items.reduce((sum, i) => sum + (i.promotionalPrice ?? i.price) * i.quantity, 0)
  );

  const messageParts: string[] = [];

  if (customerName && customerPhone) {
    messageParts.push(`*Cliente:* ${customerName}`, `*WhatsApp:* ${customerPhone}`, '');
  }

  messageParts.push('Olá! Gostaria de fazer um pedido:', '');

  if (orderId && orderId > 0) {
    messageParts.push(`*Pedido #${orderId}*`, '');
  }

  messageParts.push(...lines);
  messageParts.push('', `*Total: R$ ${total}*`);

  if (paymentEntries && paymentEntries.length > 0) {
    const paymentLines = paymentEntries.map((e) => {
      let label = PaymentMethodLabel[e.paymentMethod];
      if (e.paymentMethod === PaymentMethod.CreditCard && e.installments === 2 && !hasPromotionalItems) {
        const installmentValue = e.amountPaid / 2;
        label += ` — 2x sem juros (2x de R$ ${formatCurrency(installmentValue)})`;
      }
      return `• ${label}: R$ ${formatCurrency(e.amountPaid)}`;
    });
    messageParts.push('', '*Formas de pagamento:*', ...paymentLines);
  }

  const giftItems = items.filter((i) => i.isGift);
  if (giftItems.length > 0) {
    messageParts.push('', `*Presente:* ${giftItems.length} de ${items.length} ${giftItems.length === 1 ? 'item é' : 'itens são'} presente`);
  }

  if (observation) {
    messageParts.push('', `*Observação:* ${observation}`);
  }

  const message = messageParts.join('\n');
  const phone = seller.whatsAppPhone.startsWith("55") ?
  seller.whatsAppPhone.replace(/\D/g, '') : "55" + seller.whatsAppPhone.replace(/\D/g, '');
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

export function buildProductInquiryWhatsAppUrl(
  product: StoreProductDetail,
  displayPrice: number | null,
  selectedColor?: StoreVariantColor | null,
  selectedVariantLabel?: string | null
): string {
  const colorPart = selectedColor ? ` — Cor: ${selectedColor.name}` : '';
  const variantPart = selectedVariantLabel ? ` — ${selectedVariantLabel}` : '';
  const intro =
    displayPrice !== null
      ? `Olá! Tenho interesse neste produto: *${product.name}* (${product.sku})${colorPart}${variantPart}. `
      : `Olá! Gostaria de saber o preço deste produto: *${product.name}* (${product.sku})${colorPart}${variantPart}. `;
  const pricePart = displayPrice !== null ? `Preço: R$ ${formatCurrency(displayPrice)}. ` : '';
  const message =
    `${intro}${pricePart}Link: ${window.location.href}. Poderia me passar mais detalhes?`;

  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}
